/**
 * Textos de los mails que manda el portal (DEC-10, DEC-18). Viven acá,
 * compartidos: el modal de invitación muestra la vista previa con la misma
 * función que usa el servidor para mandarlo. Textos y diseño de Serena
 * (pestaña "Pedido · Mails del portal" del doc, 25/09), con el aspecto del
 * sitio de RASTRO: fondo crema, botón negro redondeado, firma con el logo.
 *
 *  1a. Invitación: la persona entra por primera vez.
 *  1b. Te sumé a…: la persona ya entraba y se le suma un proyecto (DEC-19).
 *  2.  Aviso de revisión: piezas o planificaciones para revisar.
 *  3.  Resumen diario (DEC-22): a la profesional de RASTRO, lo que hicieron
 *      sus clientes el día anterior.
 */

/** Texto con **negrita** (lo único que se marca). */
type Texto = string

export type Bloque =
  | { tipo: 'parrafo'; texto: Texto }
  | { tipo: 'boton'; texto: string; url: string; flecha?: boolean }
  /** Itálica gris, más chica. */
  | { tipo: 'nota'; texto: Texto }
  /** Caja resaltada con 👉. */
  | { tipo: 'destacado'; texto: Texto }
  | { tipo: 'lista'; titulo?: Texto; items: Texto[] }

export interface ContenidoMail {
  asunto: string
  /** La línea gris que se ve en la bandeja, al lado del asunto. */
  preheader: string
  /** Invitación: logo grande arriba. Los demás: logo chico en la firma. */
  logoArriba: boolean
  bloques: Bloque[]
  /** Nombre de la profesional que firma: el mail sale desde su casilla. */
  firma: string
  pie?: string
}

/** "Valentina Pérez" → "Valentina". */
const nombrePila = (nombre: string) => nombre.trim().split(/\s+/)[0] ?? ''
const saludo = (nombre: string): Bloque => ({
  tipo: 'parrafo',
  texto: nombrePila(nombre) ? `Hola, ${nombrePila(nombre)}:` : 'Hola:',
})

/* ── 1a. Invitación ─────────────────────────────────────── */

interface DatosInvitacion {
  nombre: string
  email: string
  cliente: string
  firma: string
  url: string
  /** Lo que ya está para revisar al invitar (planificaciones primero), si hay algo. */
  pendientes?: string[] | undefined
}

export function mailInvitacion(d: DatosInvitacion): ContenidoMail {
  const [primero, ...resto] = d.pendientes ?? []
  const bloques: Bloque[] = [
    saludo(d.nombre),
    {
      tipo: 'parrafo',
      texto: `Te invité al portal de **${d.cliente}**. Desde ahora, todo lo que preparamos para tu marca lo ves, lo comentás y lo aprobás en un mismo lugar.`,
    },
    { tipo: 'boton', texto: 'Aceptar invitación', url: d.url, flecha: true },
    {
      tipo: 'nota',
      // El mail con que la invitaron: con otra cuenta de Google no ve nada (§3).
      texto: `Entrás con tu cuenta de Google (${d.email}), sin crear usuario ni contraseña.`,
    },
  ]
  if (primero) {
    bloques.push({
      tipo: 'destacado',
      texto:
        resto.length > 0
          ? `**Ya te está esperando:** ${primero} y ${String(resto.length)} más.`
          : `**Ya te está esperando:** ${primero}.`,
    })
  }
  bloques.push(
    {
      tipo: 'lista',
      titulo: 'Adentro vas a encontrar:',
      items: [
        '**Tus publicaciones tal cual van a salir:** imágenes, carruseles y videos.',
        '**Comentarios y aprobaciones sobre cada pieza,** sin cadenas de mails.',
        '**Los recursos de tu marca:** manual de identidad, material de shootings y más.',
      ],
    },
    { tipo: 'parrafo', texto: 'Cualquier duda, respondé este mail.' },
  )
  return {
    asunto: `${d.firma} te invitó al portal de RASTRO | Tu contenido de ${d.cliente} ahora se aprueba desde un solo lugar`,
    preheader: 'Entrás con tu cuenta de Google, sin crear usuario ni contraseña.',
    logoArriba: true,
    bloques,
    firma: d.firma,
    pie: `Recibiste esta invitación porque ${d.firma} te sumó al portal de ${d.cliente}. Si no la esperabas, podés ignorar este mail.`,
  }
}

/* ── 1b. Te sumé a un proyecto ──────────────────────────── */

interface DatosSumado {
  nombre: string
  cliente: string
  /** Uno o más proyectos, ya unidos ("Trimestre 4 y Lanzamiento"). */
  proyecto: string
  firma: string
  url: string
}

export function mailSumado(d: DatosSumado): ContenidoMail {
  return {
    asunto: `Te sumé a ${d.proyecto} de ${d.cliente}`,
    preheader: 'Lo ves en el portal con la misma cuenta de siempre.',
    logoArriba: false,
    bloques: [
      saludo(d.nombre),
      {
        tipo: 'parrafo',
        texto: `Te sumé a **${d.proyecto}** de **${d.cliente}**. Lo ves en el portal con la misma cuenta de siempre.`,
      },
      { tipo: 'boton', texto: 'Ver en el portal', url: d.url },
      { tipo: 'parrafo', texto: 'Cualquier duda, respondé este mail.' },
    ],
    firma: d.firma,
  }
}

/* ── 2. Aviso de revisión ───────────────────────────────── */

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado']

/**
 * DEC-18: el cliente tiene 3 días hábiles (lunes a viernes, sin contar el día
 * del envío) para mirar. Sin feriados por ahora (Fase 8). "AAAA-MM-DD" → idem.
 */
export function limiteDeRevision(envio: string): string {
  const d = new Date(`${envio}T12:00:00Z`)
  let habiles = 0
  while (habiles < 3) {
    d.setUTCDate(d.getUTCDate() + 1)
    const dia = d.getUTCDay()
    if (dia !== 0 && dia !== 6) habiles++
  }
  return d.toISOString().slice(0, 10)
}

/** "2026-10-01" → "jueves 1/10". */
export function formatearLimite(fecha: string): string {
  const d = new Date(`${fecha}T12:00:00Z`)
  return `${DIAS[d.getUTCDay()] ?? ''} ${String(d.getUTCDate())}/${String(d.getUTCMonth() + 1)}`
}

/** Más de 6: los primeros 5 y "+ N más en el portal". */
const MAXIMO_LISTA = 6

interface DatosAviso {
  nombre: string
  cliente: string
  /** Planificaciones para revisar: van primero. */
  planificaciones: string[]
  piezas: string[]
  /** "AAAA-MM-DD" del día en que sale el mail. */
  envio: string
  firma: string
  url: string
}

export function mailAvisoRevision(d: DatosAviso): ContenidoMail {
  const todos = [...d.planificaciones, ...d.piezas]
  const primeraPlan = d.planificaciones[0]
  const unaPieza = d.piezas.length === 1 ? d.piezas[0] : undefined
  const asunto = primeraPlan
    ? `${primeraPlan} está lista para tu OK`
    : unaPieza
      ? `${unaPieza} está lista para tu OK`
      : `${d.cliente}: ${String(d.piezas.length)} publicaciones esperan tu OK`
  const items =
    todos.length > MAXIMO_LISTA
      ? [...todos.slice(0, 5), `+ ${String(todos.length - 5)} más en el portal`]
      : todos
  return {
    asunto,
    preheader:
      'Aprobás o comentás desde el portal, así llegamos con tiempo a las fechas.',
    logoArriba: false,
    bloques: [
      saludo(d.nombre),
      {
        tipo: 'parrafo',
        texto: `Te dejé en el portal lo nuevo de **${d.cliente}**. Si le das una mirada antes del **${formatearLimite(limiteDeRevision(d.envio))}**, llegamos con tiempo para ajustar lo que haga falta.`,
      },
      { tipo: 'boton', texto: 'Ver y aprobar', url: d.url },
      { tipo: 'lista', titulo: '**Te espera:**', items },
      {
        tipo: 'parrafo',
        texto: 'Aprobás cada una con un clic o nos dejás un comentario ahí mismo.',
      },
      {
        tipo: 'nota',
        texto:
          'Solemos trabajar con 72 horas hábiles para cada revisión: es el margen que el equipo usa para organizar los ajustes y seguir con lo que viene.',
      },
      { tipo: 'parrafo', texto: '¿Preferís charlar algo antes? Respondé este mail.' },
    ],
    firma: d.firma,
  }
}

/* ── 3. Resumen diario ──────────────────────────────────── */

export type AccionResumen = 'comento' | 'pidio_cambios' | 'aprobo'

export interface MovimientoResumen {
  /** Nombre de la persona del cliente. */
  quien: string
  accion: AccionResumen
  /** Título de la pieza o de la planificación. */
  sobre: string
  esPlanificacion: boolean
  /** El comentario (o los que acompañan un pedido de cambios). */
  texto?: string | null | undefined
  /**
   * Pieza o planificación + persona: junta un pedido de cambios con los
   * comentarios que lo acompañan. Sin clave se usa quién + sobre.
   */
  clave?: string | undefined
}

export interface ClienteResumen {
  cliente: string
  movimientos: MovimientoResumen[]
  /** Lo que sigue esperando revisión del cliente, al momento del mail. */
  esperando: { piezas: number; planificaciones: number }
}

interface DatosResumen {
  /** La profesional que lo recibe. */
  nombre: string
  /** "AAAA-MM-DD" del día que se resume (el anterior al envío). */
  fecha: string
  clientes: ClienteResumen[]
  url: string
}

/** Más de 8 movimientos de un cliente: los primeros 7 y "+ N más". */
const MAXIMO_MOVIMIENTOS = 8
const TOPE_COMENTARIO = 140

const plural = (n: number, uno: string, varios: string) =>
  `${String(n)} ${n === 1 ? uno : varios}`

const claveDe = (m: MovimientoResumen) =>
  m.clave ?? `${m.quien}|${m.sobre}|${String(m.esPlanificacion)}`

/**
 * OBS-019 — pedir cambios exige comentar antes: el pedido sale en una sola
 * línea con los comentarios que esa persona dejó ese día en esa pieza antes
 * de pedirlos. Los posteriores, y los de otras personas, quedan aparte.
 */
export function juntarPedidosDeCambios<T extends MovimientoResumen>(
  movimientos: T[],
): T[] {
  const absorbidos = new Set<number>()
  const textos = new Map<number, string>()
  movimientos.forEach((m, i) => {
    if (m.accion !== 'pidio_cambios') return
    const propios: string[] = []
    for (let j = 0; j < i; j++) {
      const c = movimientos[j]
      if (c?.accion !== 'comento' || absorbidos.has(j) || claveDe(c) !== claveDe(m))
        continue
      absorbidos.add(j)
      const t = (c.texto ?? '').trim()
      if (t) propios.push(t)
    }
    if (propios.length > 0) textos.set(i, propios.join(' · '))
  })
  return movimientos
    .map((m, i) => (textos.has(i) ? { ...m, texto: textos.get(i) } : m))
    .filter((_, i) => !absorbidos.has(i))
}

/** "Valentina comentó «Carta de otoño»: “Cambiaría la foto”". */
export function lineaDeMovimiento(m: MovimientoResumen): string {
  const quien = nombrePila(m.quien) || m.quien
  const sobre = `«${m.sobre}»${m.esPlanificacion ? ' (planificación)' : ''}`
  const t = (m.texto ?? '').replace(/\s+/g, ' ').trim()
  const cita = t
    ? `: “${t.length > TOPE_COMENTARIO ? `${t.slice(0, TOPE_COMENTARIO - 1)}…` : t}”`
    : ''
  switch (m.accion) {
    case 'aprobo':
      return `**${quien}** aprobó ${sobre}`
    case 'pidio_cambios':
      return `**${quien}** pidió cambios en ${sobre}${cita}`
    case 'comento':
      return `**${quien}** comentó ${sobre}${cita}`
  }
}

/** "Sigue esperando revisión: 3 publicaciones y 1 planificación." */
export function lineaEsperando(e: ClienteResumen['esperando']): string {
  const partes = [
    ...(e.piezas > 0 ? [plural(e.piezas, 'publicación', 'publicaciones')] : []),
    ...(e.planificaciones > 0
      ? [plural(e.planificaciones, 'planificación', 'planificaciones')]
      : []),
  ]
  return partes.length === 0
    ? 'No le queda nada esperando revisión.'
    : `Sigue esperando revisión: ${partes.join(' y ')}.`
}

/**
 * DEC-22 — sale a las 8 con lo del día anterior: comentarios, pedidos de
 * cambios y aprobaciones, agrupados por cliente, y lo que cada cliente sigue
 * teniendo para revisar. Los días sin actividad no sale.
 */
export function mailResumenDiario(datos: DatosResumen): ContenidoMail {
  const d = {
    ...datos,
    clientes: datos.clientes.map((c) => ({
      ...c,
      movimientos: juntarPedidosDeCambios(c.movimientos),
    })),
  }
  const total = d.clientes.reduce((n, c) => n + c.movimientos.length, 0)
  const dia = formatearLimite(d.fecha)
  const bloques: Bloque[] = [
    saludo(d.nombre),
    {
      tipo: 'parrafo',
      texto: `Esto hicieron tus clientes en el portal el **${dia}**:`,
    },
  ]
  for (const c of d.clientes) {
    const lineas = c.movimientos.map(lineaDeMovimiento)
    const items =
      lineas.length > MAXIMO_MOVIMIENTOS
        ? [
            ...lineas.slice(0, MAXIMO_MOVIMIENTOS - 1),
            `+ ${String(lineas.length - MAXIMO_MOVIMIENTOS + 1)} más en el portal`,
          ]
        : lineas
    bloques.push(
      { tipo: 'lista', titulo: `**${c.cliente}**`, items },
      { tipo: 'nota', texto: lineaEsperando(c.esperando) },
    )
  }
  bloques.push({ tipo: 'boton', texto: 'Abrir el portal', url: d.url })
  return {
    asunto: `Resumen del ${dia}: ${plural(total, 'movimiento', 'movimientos')} de tus clientes`,
    preheader:
      d.clientes.length === 1
        ? `${d.clientes[0]?.cliente ?? ''}: comentarios, cambios pedidos y aprobaciones.`
        : `${plural(d.clientes.length, 'cliente', 'clientes')}: comentarios, cambios pedidos y aprobaciones.`,
    logoArriba: false,
    bloques,
    firma: 'Portal de clientes',
    pie: 'Este resumen sale todos los días a las 8 con lo que hicieron tus clientes el día anterior. Los días sin movimientos no sale.',
  }
}

/* ── HTML y texto ───────────────────────────────────────── */

const escapar = (t: string) =>
  t
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

/** Escapa y convierte **negrita**. */
const conFormato = (t: string) =>
  escapar(t).replace(/\*\*(.+?)\*\*/g, '<strong style="font-weight:700">$1</strong>')

export const sinFormato = (t: string) => t.replace(/\*\*(.+?)\*\*/g, '$1')

/** Los colores del sitio de RASTRO. */
const C = {
  crema: '#f5f2ea',
  cremaOscuro: '#ebe6da',
  tinta: '#161616',
  suave: '#6b6760',
  linea: '#d9d3c6',
}
const LETRA = "font-family:'Helvetica Neue',Helvetica,Arial,sans-serif"

function bloqueHtml(b: Bloque): string {
  const p = `margin:0 0 18px;font-size:16px;line-height:1.55;color:${C.tinta}`
  switch (b.tipo) {
    case 'parrafo':
      return `<p style="${p}">${conFormato(b.texto)}</p>`
    case 'nota':
      return `<p style="margin:-6px 0 22px;font-size:14px;line-height:1.5;font-style:italic;color:${C.suave}">${conFormato(b.texto)}</p>`
    case 'destacado':
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 22px"><tr><td style="background:${C.cremaOscuro};border-radius:12px;padding:18px 20px"><table role="presentation" cellpadding="0" cellspacing="0"><tr><td valign="top" style="font-size:20px;line-height:1.4;padding-right:14px">👉</td><td style="font-size:16px;line-height:1.5;color:${C.tinta}">${conFormato(b.texto)}</td></tr></table></td></tr></table>`
    case 'boton': {
      const flecha = b.flecha
        ? `<td style="padding-left:28px"><span style="display:inline-block;width:34px;height:34px;line-height:34px;border-radius:17px;background:#ffffff;color:${C.tinta};text-align:center;font-size:18px;font-weight:700">&rarr;</span></td>`
        : ''
      return `<table role="presentation" cellpadding="0" cellspacing="0" style="margin:4px 0 22px"><tr><td style="background:${C.tinta};border-radius:999px"><a href="${escapar(b.url)}" style="display:block;padding:${b.flecha ? '9px 10px 9px 26px' : '15px 34px'};color:#ffffff;text-decoration:none;font-size:16px;font-weight:600"><table role="presentation" cellpadding="0" cellspacing="0"><tr><td style="color:#ffffff;font-size:16px;font-weight:600">${escapar(b.texto)}</td>${flecha}</tr></table></a></td></tr></table>`
    }
    case 'lista': {
      const titulo = b.titulo
        ? `<p style="margin:0 0 10px;font-size:16px;line-height:1.5;color:${C.tinta}">${conFormato(b.titulo)}</p>`
        : ''
      const items = b.items
        .map(
          (i) =>
            `<tr><td valign="top" style="width:22px;font-size:16px;line-height:1.5;color:${C.tinta}">&bull;</td><td style="padding:0 0 8px;font-size:16px;line-height:1.5;color:${C.tinta}">${conFormato(i)}</td></tr>`,
        )
        .join('')
      return `${titulo}<table role="presentation" cellpadding="0" cellspacing="0" style="margin:0 0 18px 6px">${items}</table>`
    }
  }
}

/**
 * Versión HTML, con estilos en línea (los clientes de correo no leen CSS) y
 * letra estándar (Gmail no carga tipografías). `logo`: URL pública del PNG.
 */
export function aHtml(c: ContenidoMail, opciones: { logo: string }): string {
  const logoArriba = c.logoArriba
    ? `<p style="margin:0 0 30px"><img src="${escapar(opciones.logo)}" width="220" alt="RASTRO" style="display:block;width:220px;max-width:70%;height:auto;border:0"></p>`
    : ''
  const marca = c.logoArriba
    ? `<span style="color:${C.tinta}">RASTRO&reg;</span>`
    : `<img src="${escapar(opciones.logo)}" width="120" alt="RASTRO" style="display:inline-block;width:120px;height:auto;border:0;vertical-align:middle">`
  const firma = `<p style="margin:10px 0 4px;font-size:16px;font-weight:700;color:${C.tinta}">${escapar(c.firma)}</p>
<p style="margin:0;font-size:15px;color:${C.tinta}">${marca} &nbsp;&middot;&nbsp; <a href="https://somosrastro.com.ar" style="color:${C.suave}">somosrastro.com.ar</a></p>`
  const pie = c.pie
    ? `<p style="margin:26px 0 0;padding-top:16px;border-top:1px solid ${C.linea};font-size:12px;line-height:1.5;color:${C.suave}">${escapar(c.pie)}</p>`
    : ''
  return `<!doctype html><html lang="es"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta name="color-scheme" content="light"></head>
<body style="margin:0;padding:0;background:#ffffff;${LETRA}">
<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent">${escapar(c.preheader)}</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr><td align="center" style="padding:16px 8px">
<!-- Ancho fijo y centrado con align + margin: Gmail corre a la derecha una tarjeta de ancho 100%. -->
<table role="presentation" align="center" width="600" cellpadding="0" cellspacing="0" style="width:100%;max-width:600px;margin:0 auto;background:${C.crema};border-radius:16px">
<tr><td style="padding:40px 36px 34px;text-align:left;${LETRA}">
${logoArriba}${c.bloques.map(bloqueHtml).join('\n')}
${firma}
${pie}
</td></tr></table>
</td></tr></table>
</body></html>`
}

/** Versión en texto plano, para los clientes de correo que no muestran HTML. */
export function aTexto(c: ContenidoMail): string {
  const lineas: string[] = []
  for (const b of c.bloques) {
    if (b.tipo === 'boton') lineas.push(`${b.texto}: ${b.url}`, '')
    else if (b.tipo === 'lista') {
      if (b.titulo) lineas.push(sinFormato(b.titulo))
      lineas.push(...b.items.map((i) => `- ${sinFormato(i)}`), '')
    } else lineas.push(sinFormato(b.texto), '')
  }
  lineas.push(c.firma, 'RASTRO® · somosrastro.com.ar')
  if (c.pie) lineas.push('', c.pie)
  return lineas.join('\n')
}
